<template>
	<div class="checkout-form">
		<div class="checkout-form__wrapper insicht insicht--fade-in insicht--fast">
			<div class="checkout-form__container">
				<ShopHeader
					class="checkout-form__header"
					v-if="!isLoggedIn"
					title=""
					sub-title=""
					:login-text="this.$t('shoplist.shopHeader.loginTextCheckout')"
          :disabled="formLocked"
				/>

				<h1 class="checkout-form__headline" v-html="$t('checkout.title')"/>

				<Button
					v-if="!cartIsDetached"
					@click.prevent="backToShop"
					class="checkout-form__back-to-shop checkout-form__back-to-shop--top"
					level="tertiary"
					type="button"
					:text="$t('checkout.continueShopping')"
					arrow
					reverse
          :disabled="formLocked"
				/>

        <GeneralMessage
          v-if="cartGlobalMessage"
          :message="cartGlobalMessage"
          type="warning"
          stick
        />

        <GeneralMessage
          v-for="(warning, i) in cartWarnings"
          :key="`${warning.code}${i}`"
          :message="warning.message"
          type="warning"
          stick
        />

				<GeneralMessage
					v-for="(message, i) in notifyMessages"
					ref="notificationMessages"
					:key="'message'+i+message.message"
					:message="message.message"
					:type="message.type"
				/>

				<GeneralMessage
					v-for="(message, i) in checkoutMessagesNormal"
					ref="checkoutMessages"
					:key="'checkoutmessage'+i+message.message"
					:message="message.message"
					:type="message.type"
				/>

				<GeneralMessage
					v-if="cartOrderTimePassed"
					ref="notificationMessages"
					:message="$t('checkout.errorTimePassed')"
					type="error"
					@click="onTimeEdit"
				/>

				<CheckoutCartButton
					show-tip
					show-co2
				/>

				<ValidationObserver
					ref="form"
					v-slot="{ invalid, errors }"
					:tag="!cartIsUberDirect || addressSet ? 'form' : 'div'"
					class="checkout-form__form-wrapper"
					@submit.prevent="submit"
					novalidate
				>
					<div
						class="checkout-form__block"
						v-if="showEmail || !shippingAddressDisabled"
					>
						<h2
              v-if="!cartIsUberDirect || !addressSet"
              class="checkout-form__block-title"
              v-html="addressTitle"
            />

						<template v-if="isOutdoorDelivery">
							<div class="checkout-form__outdoor-delivery">
								<figure
									v-if="cartOutdoorDeliveryPoint.image && cartOutdoorDeliveryPoint.image.path"
									class="checkout-form__outdoor-delivery-image"
								>
									<img
										class="checkout-form__outdoor-delivery-image-element"
										:src="cartOutdoorDeliveryPoint.image.path"
										alt="outdoor delivery point image"
									/>
								</figure>
								<address
									class="checkout-form__outdoor-delivery-address"
									v-sanitize="outdoorDeliveryAddress"
								/>
								<Button
									class="checkout-form__outdoor-delivery-link"
									type="button"
									:href="`http://www.google.com/maps/place/${cartOutdoorDeliveryPoint.latitude},${cartOutdoorDeliveryPoint.longitude}`"
									target="_blank"
									level="tertiary"
									arrow
									:text="$t('checkout.outdoorDelivery.googleMapsLink')"
								/>
							</div>

							<h3 class="checkout-form__block-subtitle" v-html="$t('checkout.personalData')"/>
						</template>
            <template v-if="!addressSet || !cartIsUberDirect">
              <ValidationObserver
                ref="shippingAddressForm"
                v-slot="{ invalid, errors }"
                :tag="cartIsUberDirect ? 'form' : 'div'"
                class="checkout-form__form-wrapper"
                @submit.prevent.stop="onShippingAddress"
                novalidate
              >
                <div
                  class="input-row"
                  v-if="showEmail"
                >
                  <ValidationProvider
                    class="input-row__wrapper"
                    name="email"
                    :rules="shippingAddressLocked ? '' : 'required|email'"
                    v-slot="{ errors, invalid, validated }"
                    tag="div"
                    :customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
                  >
                    <FormInput
                      type="email"
                      name="email"
                      autocomplete="email"
                      :placeholder="$t('forms.labels.email')"
                      :label="$t('forms.labels.email')"
                      v-model="form.email"
                      :error="errors.length > 0"
                      :readonly="!!isLoggedIn"
                      :disabled="formLocked || shippingAddressLocked"
                      :required="!shippingAddressLocked"
                    />
                    <span
                      class="input-error"
                      v-if="invalid && validated"
                      v-html="errors[0]"
                    />
                    <span
                      v-if="isOrderTypeDonation"
                      class="additional-info"
                      v-html="$t('checkout.emailHintDonation')"
                    />
                    <span
                      v-else
                      class="additional-info"
                      v-html="$t('checkout.emailHint')"
                    />
                  </ValidationProvider>
                </div>

                <ValidationObserver
                  v-if="!shippingAddressDisabled"
                  tag="section"
                  ref="shippingAddress"
                  vid="shippingAddress"
                  v-slot="{ invalid }"
                >
                  <div class="input-row">
                    <ValidationProvider
                      class="input-row__wrapper"
                      name="shippingFirstname"
                      :rules="shippingAddressLocked ? '' : 'required'"
                      v-slot="{ errors, invalid, validated }"
                      tag="div"
                      :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                    >
                      <FormInput
                        type="text"
                        :placeholder="$t('forms.labels.firstname')"
                        :label="$t('forms.labels.firstname')"
                        name="firstname"
                        v-model="form.shippingAddress.firstName"
                        :error="errors.length > 0"
                        :disabled="formLocked || shippingAddressLocked"
                        :required="!shippingAddressLocked"
                      />
                      <span
                        class="input-error"
                        v-if="invalid && validated"
                        v-html="errors[0]"
                      />
                    </ValidationProvider>
                    <ValidationProvider
                      class="input-row__wrapper"
                      name="shippingLastname"
                      :rules="shippingAddressLocked ? '' : 'required'"
                      v-slot="{ errors, invalid, validated }"
                      tag="div"
                      :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                    >
                      <FormInput
                        type="text"
                        :placeholder="$t('forms.labels.lastname')"
                        :label="$t('forms.labels.lastname')"
                        name="lastname"
                        v-model="form.shippingAddress.lastName"
                        :error="errors.length > 0"
                        :disabled="formLocked || shippingAddressLocked"
                        :required="!shippingAddressLocked"
                      />
                      <span
                        class="input-error"
                        v-if="invalid && validated"
                        v-html="errors[0]"
                      />
                    </ValidationProvider>
                  </div>

                  <div
                    class="input-row"
                    v-if="showAddress"
                  >
                    <FormInput
                      type="text"
                      :placeholder="$t('forms.labels.company')"
                      :label="$t('forms.labels.company')"
                      :labelOption="$t('forms.labels.optional')"
                      name="company"
                      v-model="form.shippingAddress.company"
                      :disabled="formLocked || shippingAddressLocked"
                    />
                  </div>

                  <div
                    class="input-row"
                    v-if="showAddress"
                  >
                    <ValidationObserver
                      class="input-row__wrapper"
                      tag="div"
                      ref="shippingStreetNr"
                      v-slot="{ validated, invalid, errors }"
                    >
                      <div class="input-group">
                        <ValidationProvider
                          class="input-group__span-9"
                          name="shippingStreet"
                          :rules="addressRequired ? 'required' : ''"
                          v-slot="{ errors, invalid }"
                          tag="div"
                          :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                        >
                          <FormInput
                            type="text"
                            :placeholder="$t('forms.labels.street')"
                            :label="$t('forms.labels.street')"
                            :labelOption="addressRequired ? '' : $t('forms.labels.optional')"
                            name="street"
                            v-model="form.shippingAddress.street"
                            :error="errors.length > 0"
                            :required="addressRequired"
                            :disabled="formLocked || shippingAddressLocked"
                          />
                        </ValidationProvider>
                        <ValidationProvider
                          class="input-group__span-3"
                          name="shippingStreetNumber"
                          :rules="addressRequired ? 'required' : ''"
                          v-slot="{ errors, invalid }"
                          tag="div"
                          :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                        >
                          <FormInput
                            type="text"
                            :placeholder="$t('forms.labels.number')"
                            :label="$t('forms.labels.number')"
                            :labelOption="addressRequired ? '' : $t('forms.labels.optional')"
                            name="number"
                            v-model="form.shippingAddress.streetNumber"
                            :error="errors.length > 0"
                            :required="addressRequired"
                            :disabled="formLocked || shippingAddressLocked"
                          />
                        </ValidationProvider>
                      </div>
                      <div
                        class="input-error input-group__span-12"
                        v-if="invalid && validated"
                        v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
                      />
                    </ValidationObserver>

                    <ValidationObserver
                      class="input-row__wrapper"
                      tag="div"
                      ref="shippingZipCity"
                      v-slot="{ dirty, validated, invalid, errors }"
                    >
                      <div class="input-group">
                        <ValidationProvider
                          class="input-group__span-4 input-group__span-xxl-3"
                          name="shippingZip"
                          :rules="addressRequired ? 'required|digits:4' : (shippingAddressLocked ? '' : 'digits:4')"
                          v-slot="{ errors, invalid }"
                          tag="div"
                          :customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
                        >
                          <FormInput
                            ref="shippingZipInput"
                            type="text"
                            v-model="form.shippingAddress.postcode"
                            @keydown="onZipKeydown"
                            @input="onZipInput"
                            :placeholder="$t('forms.labels.zip')"
                            :label="$t('forms.labels.zip')"
                            :labelOption="addressRequired ? '' : $t('forms.labels.optional')"
                            name="zip"
                            :minlength="4"
                            :maxlength="4"
                            inputmode="decimal"
                            pattern="[0-9]{4}"
                            :error="errors.length > 0"
                            :required="addressRequired"
                            :readonly="zipLocked"
                            :disabled="formLocked || shippingAddressLocked"
                          />
                        </ValidationProvider>
                        <ValidationProvider
                          class="input-group__span-8 input-group__span-xxl-9"
                          name="shippingCity"
                          :rules="addressRequired ? 'required' : ''"
                          v-slot="{ errors, invalid }"
                          tag="div"
                          :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                        >
                          <FormInput
                            type="text"
                            :placeholder="$t('forms.labels.city')"
                            :label="$t('forms.labels.city')"
                            :labelOption="addressRequired ? '' : $t('forms.labels.optional')"
                            name="city"
                            v-model="form.shippingAddress.city"
                            :error="errors.length > 0"
                            :required="addressRequired"
                            :disabled="formLocked || shippingAddressLocked"
                          />
                        </ValidationProvider>
                      </div>
                      <div
                        class="input-error input-group__span-12"
                        v-if="invalid && (validated || dirty)"
                        v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
                      />
                    </ValidationObserver>

                  </div>
                  <div
                    class="input-row"
                    v-if="showAddress"
                  >
                    <div class="input-row__wrapper">
                      <FormInput
                        type="text"
                        :placeholder="$t('forms.labels.addressDetails')"
                        :label="$t('forms.labels.addressDetails')"
                        :labelOption="$t('forms.labels.optional')"
                        name="addressdetails"
                        v-model="form.shippingAddress.details"
                        :disabled="formLocked || shippingAddressLocked"
                        :maxlength="addressDetailLimit"
                        show-charsleft
                      />
                      <strong
                        v-if="showAddressDetailsHint"
                        class="additional-info text--highlight"
                        v-html="$t('checkout.addressDetailsHint')"
                      />
                    </div>
                    <div class="input-row__wrapper">
                      <FormInputSelect
                        :options="countryOptions"
                        v-model="form.shippingAddress.countryCode"
                        :label="$t('forms.labels.country')"
                        name="country"
                        :disabled="formLocked || shippingAddressLocked"
                      />
                    </div>
                  </div>

                  <div class="input-row">
                    <ValidationProvider
                      class="input-row__wrapper"
                      name="shippingPhone"
                      :rules="shippingAddressLocked ? '' : { required: !isOrderTypeDonation, phone: true }"
                      v-slot="{ errors, invalid, validated }"
                      tag="div"
                      :customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
                    >
                      <FormInput
                        type="tel"
                        :placeholder="$t('forms.labels.phone')"
                        :label="$t('forms.labels.phone')"
                        :labelOption="!isOrderTypeDonation ? '' : $t('forms.labels.optional')"
                        name="phone"
                        v-model="form.shippingAddress.phoneNumber"
                        :error="errors.length > 0"
                        :disabled="formLocked || shippingAddressLocked"
                        :required="!shippingAddressLocked && !isOrderTypeDonation"
                      />
                      <span
                        class="input-error"
                        v-if="invalid && validated"
                        v-html="errors[0]"
                      />
                      <span
                        v-if="!isOrderTypeDonation"
                        class="additional-info"
                        v-html="$t('checkout.phoneHint')"
                      />
                    </ValidationProvider>
                  </div>

                  <FormInputCheckbox
                    v-if="isOrderTypeDonation"
                    name="anonymousDonation"
                    value="anonymousDonation"
                    v-model="form.anonymousDonation"
                    :disabled="formLocked"
                    immediate
                  >
                    <span v-html="$t('checkout.anonymousDonationLabel')"/>
                  </FormInputCheckbox>

                </ValidationObserver>

                <Button
                  v-if="cartIsUberDirect"
                  :text="$t('checkout.continueButton')"
                  level="primary"
                  type="submit"
                />
              </ValidationObserver>
            </template>
            <template v-if="addressSet && cartIsUberDirect">
              <h2
                class="checkout-form__block-title"
                v-html="addressTitle"
              />
              <div class="input-row">
                <div class="input-row__wrapper">
                  <address
                    class="checkout-form__block-text"
                    v-html="formatAddress(form.shippingAddress, { exclude: ['phoneNumber']})"
                  />
                  <address
                    class="checkout-form__block-text"
                    v-html="formatLines([form.email, form.shippingAddress.phoneNumber])"
                  />
                  <Button
                    class="checkout-form__address-edit"
                    level="tertiary"
                    type="button"
                    :text="$t('checkout.changeShippingAddress')"
                    arrow
                    :disabled="formLocked"
                    @click.prevent="onShippingAddressEdit"
                  />
                </div>
                <div
                  v-if="cartShippingCoordinates"
                  class="input-row__wrapper"
                >
                  <MapLocations
                    :key="cartShippingCoordinates ? [cartShippingCoordinates.lat, cartShippingCoordinates.lng].join(',') : ''"
                    :locations="[{
                      id: 1,
                      title: addressTitle,
                      ...cartShippingCoordinates,
                    }]"
                  />
                </div>
              </div>
              <div class="input-row">
                <div class="checkout-form__info">
                  <h3
                    class="checkout-form__info-title"
                    v-html="$t('uberDirect.deliveryUpdated')"
                  />
                  <ul class="checkout-form__info-items">
                    <li
                      v-if="deliveryTimeEnhanced && !timeDisabled"
                      class="checkout-form__info-item"
                    >
                      <SVGClock
                        class="checkout-form__info-item-icon"
                      />
                      <span
                        class="checkout-form__info-item-text"
                        v-html="deliveryTimeEnhanced"
                      />
                    </li>
                    <li
                      v-if="deliveryPrice > 0"
                      class="checkout-form__info-item"
                    >
                      <SVGMoney
                        class="checkout-form__info-item-icon"
                      />
                      <span
                        class="checkout-form__info-item-text"
                        v-html="$t('cart.deliveryFee') + ' ' + $options.filters.fwgpricecent(deliveryPrice, 'html')"
                      />
                    </li>
                  </ul>
                </div>
              </div>
            </template>
					</div>

          <template v-if="addressSet || !cartIsUberDirect">
            <div
              v-if="hasFeature('FEATURE_ACCOUNT_CREATE_CHECKOUT') && !isLoggedIn"
              class="checkout-frm__block"
            >
              <h2
                class="checkout-form__block-title"
                v-html="$t('checkout.account.title')"
              />
              <FormInputCheckbox
                name="createUser"
                value="createUser"
                v-model="form.createUser"
                :disabled="formLocked"
                immediate
              >
                <span v-html="$t('checkout.account.label')"/>
              </FormInputCheckbox>
              <span class="additional-info" v-html="$t('checkout.account.hint')"/>
            </div>

            <div
              v-if="showReusableDishes"
              class="checkout-form__block"
            >
              <h2 class="checkout-form__block-title" v-html="$t('checkout.reusableDishes.title')"/>
              <FormInputCheckbox
                name="reusableDishes"
                value="reusableDishes"
                v-model="form.useDishes"
                immediate
                :disabled="formLocked || reusableDishLocked"
              >
                <span v-html="$t('checkout.reusableDishes.label')"/>
              </FormInputCheckbox>

              <!-- <div
                v-if="form.useDishes"
                class="checkout-form__dish-info input-row"
              >
                <div class="input-row__wrapper">
                  <div class="checkout-form__dish-info-wrapper">
                    <SVGDishBowl class="checkout-form__dish-info-icon"/>
                    <div class="checkout-form__dish-info-content">
                      <h3
                        class="checkout-form__dish-info-title"
                        v-html="$t('checkout.reusableDishes.infoTitle')"
                      />
                      <div
                        class="checkout-form__dish-info-text"
                        v-html="$t('checkout.reusableDishes.intoText')"
                      />
                    </div>
                  </div>
                  <ValidationProvider
                    rules="required"
                    class="checkout-form__dish-info-input"
                    name="dishReturn"
                    tag="div"
                    v-slot="{ invalid, errors, validated }"
                    :customMessages="{ required: $t('checkout.reusableDishes.returnError') }"
                  >
                    <FormInputPayment
                      v-model="form.dishReturnMethod"
                      :methods="dishReturnMethods"
                      :disabled="formLocked || reusableDishLocked"
                    />
                    <span
                      class="input-error"
                      v-if="invalid && validated"
                      v-html="errors[0]"
                    />
                  </ValidationProvider>
                </div>
              </div> -->
            </div>

            <div
              class="checkout-form__block"
              v-if="!timeDisabled"
            >
              <h2 class="checkout-form__block-title" v-html="timeTitle"/>
              <GeneralMessage
                v-if="cartBigOrderText"
                ref="notificationMessages"
                :message="cartBigOrderText"
                type="warning"
                stick
              />
              <div class="checkout-form__time">
                <SVGClock
                  class="checkout-form__time-icon"
                />
                <span
                  class="checkout-form__time-text"
                  v-html="deliveryTimeEnhanced"
                />
              </div>
              <Button
                v-if="!isIndining && !timeLocked"
                class="checkout-form__time-edit"
                type="button"
                @click.prevent="onTimeEdit"
                level="tertiary"
                arrow
                :text="timeEditLabel"
                :disabled="formLocked"
              />
            </div>

            <div
              class="checkout-form__block"
              v-if="isDelivery && !dropoffDisabled"
            >
              <h2 class="checkout-form__block-title" v-html="$t('checkout.dropoff.title')"/>
              <div class="input-row">
                <ValidationProvider
                  class="input-row__wrapper"
                  name="dropoffType"
                  :rules="dropoffLocked ? '' : 'required'"
                  v-slot="{ errors, invalid, validated }"
                  tag="div"
                  :customMessages="{ required: $t('checkout.dropoff.error') }"
                >
                  <FormInputRadio
                    name="dropoffType"
                    v-model="form.dropoffType"
                    :options="dropoffOptions"
                    vertical
                    :required="!dropoffLocked"
                    :disabled="formLocked || dropoffLocked"
                  />
                  <span
                    class="input-error"
                    v-if="invalid && validated"
                    v-html="errors[0]"
                  />
                </ValidationProvider>
              </div>
            </div>

            <div
              class="checkout-form__block"
              v-if="!notesDisabled"
            >
              <h2 class="checkout-form__block-title" v-html="$t('checkout.notesTitle')"/>
              <div class="input-row">
                <FormInputTextarea
                  :placeholder="$t('forms.labels.notes')"
                  :label="$t('forms.labels.notes')"
                  v-model="form.notes"
                  :disabled="formLocked || notesLocked"
                  :maxlength="notesLimit"
                  show-charsleft
                />
              </div>
            </div>

            <div
              class="checkout-form__block"
              ref="payments"
              v-if="checkoutPayments.length && !paymentDisabled"
            >
              <h2 class="checkout-form__block-title" v-html="$t('checkout.paymentTitle')"/>

              <template v-if="showSavedPayments">
                <div class="input-row">
                  <ValidationProvider
                    rules=""
                    name="savedPayment"
                    tag="div"
                    v-slot="{ invalid, errors, validated }"
                    :customMessages="{ required: $t('checkout.errorPaymentMethod') }"
                  >
                    <CheckoutSavedPayments
                      :payments="savedPayments"
                      :disabled="formLocked || paymentLocked"
                      v-model="selectedSavedPaymentToken"
                      @delete="onDeletePayment"
                    />
                    <span
                      class="input-error"
                      v-if="invalid && validated"
                      v-html="errors[0]"
                    />
                  </ValidationProvider>
                </div>
                <Button
                  v-if="!showPaymentMethods"
                  @click.prevent="onShowPaymentMethods"
                  type="button"
                  level="tertiary"
                  :text="$t('checkout.savedPayments.changeMethod')"
                  arrow
                  :disabled="formLocked"
                />
              </template>

              <div
                v-if="(!showSavedPayments || showPaymentMethods || !savedPayments.length)"
                class="input-row"
              >
                <ValidationProvider
                  v-if="checkoutPaymentsVisible.length"
                  :rules="paymentLocked ? '' : 'required'"
                  name="paymentCode"
                  tag="div"
                  v-slot="{ invalid, errors, validated }"
                  :customMessages="{ required: $t('checkout.errorPaymentMethod') }"
                >
                  <FormInputPayment
                    v-model="form.paymentCode"
                    :methods="checkoutPaymentsVisible"
                    :disabled="formLocked || paymentLocked"
                    @input="onPaymentChanged"
                  />
                  <span
                    class="input-error"
                    v-if="invalid && validated"
                    v-html="errors[0]"
                  />
                </ValidationProvider>
              </div>

            </div>

            <div
              class="checkout-form__block"
              v-if="showBillingAddress && !billingAddressDisabled"
            >
              <h2 class="checkout-form__block-title" v-html="$t('checkout.billingAddress')"/>

              <FormInputCheckbox
                v-if="isDelivery"
                name="billingAddressIdentical"
                value="billingAddressIdentical"
                v-model="form.billingAddressIdentical"
                :disabled="formLocked || billingAddressLocked"
                immediate
              >
                <span v-html="$t('checkout.billingAddressIdentical')"/>
              </FormInputCheckbox>




              <ValidationObserver
                tag="section"
                ref="billingAddress"
                vid="billingAddress"
                v-slot="{ invalid }"
                v-if="!form.billingAddressIdentical || !isDelivery"
              >
                <div class="input-row">
                  <ValidationProvider
                    class="input-row__wrapper"
                    name="billingFirstname"
                    :rules="billingAddressLocked ? '' : 'required'"
                    v-slot="{ errors, invalid, validated }"
                    tag="div"
                    :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                  >
                    <FormInput
                      type="text"
                      :placeholder="$t('forms.labels.firstname')"
                      :label="$t('forms.labels.firstname')"
                      name="firstname"
                      v-model="form.billingAddress.firstName"
                      :error="errors.length > 0"
                      :disabled="formLocked || billingAddressLocked"
                      :required="!billingAddressLocked"
                    />
                    <span
                      class="input-error"
                      v-if="invalid && validated"
                      v-html="errors[0]"
                    />
                  </ValidationProvider>
                  <ValidationProvider
                    class="input-row__wrapper"
                    name="billingLastname"
                    :rules="billingAddressLocked ? '' : 'required'"
                    v-slot="{ errors, invalid, validated }"
                    tag="div"
                    :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                  >
                    <FormInput
                      type="text"
                      :placeholder="$t('forms.labels.lastname')"
                      :label="$t('forms.labels.lastname')"
                      name="lastname"
                      v-model="form.billingAddress.lastName"
                      :error="errors.length > 0"
                      :disabled="formLocked || billingAddressLocked"
                      :required="!billingAddressLocked"
                    />
                    <span
                      class="input-error"
                      v-if="invalid && validated"
                      v-html="errors[0]"
                    />
                  </ValidationProvider>
                </div>

                <div class="input-row">
                  <FormInput
                    type="text"
                    :placeholder="$t('forms.labels.company')"
                    :label="$t('forms.labels.company')"
                    :labelOption="$t('forms.labels.optional')"
                    name="company"
                    v-model="form.billingAddress.company"
                    :disabled="formLocked || billingAddressLocked"
                  />
                </div>

                <div class="input-row">
                  <ValidationObserver
                    class="input-row__wrapper"
                    tag="div"
                    ref="billingStreetNr"
                    v-slot="{ validated, invalid, errors }"
                  >
                    <div class="input-group">
                      <ValidationProvider
                        class="input-group__span-9"
                        name="billingStreet"
                        :rules="billingAddressLocked ? '' : 'required'"
                        v-slot="{ errors, invalid }"
                        tag="div"
                        :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                      >
                        <FormInput
                          type="text"
                          :placeholder="$t('forms.labels.street')"
                          :label="$t('forms.labels.street')"
                          name="street"
                          v-model="form.billingAddress.street"
                          :error="errors.length > 0"
                          :disabled="formLocked || billingAddressLocked"
                          :required="!billingAddressLocked"
                        />
                      </ValidationProvider>
                      <ValidationProvider
                          class="input-group__span-3"
                          name="billingStreetNumber"
                          :rules="billingAddressLocked ? '' : 'required'"
                          v-slot="{ errors, invalid }"
                          tag="div"
                          :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                        >
                        <FormInput
                          type="text"
                          :placeholder="$t('forms.labels.number')"
                          :label="$t('forms.labels.number')"
                          name="number"
                          v-model="form.billingAddress.streetNumber"
                          :error="errors.length > 0"
                          :disabled="formLocked || billingAddressLocked"
                          :required="!billingAddressLocked"
                        />
                      </ValidationProvider>
                    </div>
                    <div
                      class="input-error input-group__span-12"
                      v-if="invalid && validated"
                      v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
                    />
                  </ValidationObserver>

                  <ValidationObserver
                    class="input-row__wrapper"
                    tag="div"
                    ref="billingZipCity"
                    v-slot="{ dirty, validated, invalid, errors }"
                  >
                    <div class="input-group">
                      <ValidationProvider
                        class="input-group__span-4 input-group__span-xxl-3"
                        name="billingZip"
                        :rules="billingAddressLocked ? '' : 'required|digits:4'"
                        v-slot="{ errors, invalid }"
                        tag="div"
                        :customMessages="{ required: $t('forms.errors.fieldEmpty'), digits: $t('forms.errors.zip') }"
                      >
                        <FormInput
                          ref="billingZipInput"
                          type="text"
                          v-model="form.billingAddress.postcode"
                          @keydown="onZipKeydown"
                          @input="onZipInputBilling"
                          name="zip"
                          :placeholder="$t('forms.labels.zip')"
                          :label="$t('forms.labels.zip')"
                          :minlength="4"
                          :maxlength="4"
                          inputmode="decimal"
                          pattern="[0-9]{4}"
                          :error="errors.length > 0"
                          :disabled="formLocked || billingAddressLocked"
                          :required="!billingAddressLocked"
                        />
                      </ValidationProvider>
                      <ValidationProvider
                        class="input-group__span-8 input-group__span-xxl-9"
                        name="billingCity"
                        :rules="billingAddressLocked ? '' : 'required'"
                        v-slot="{ errors, invalid }"
                        tag="div"
                        :customMessages="{ required: $t('forms.errors.fieldEmpty') }"
                      >
                        <FormInput
                          type="text"
                          :placeholder="$t('forms.labels.city')"
                          :label="$t('forms.labels.city')"
                          name="city"
                          v-model="form.billingAddress.city"
                          :error="errors.length > 0"
                          :disabled="formLocked || billingAddressLocked"
                          :required="!billingAddressLocked"
                        />
                      </ValidationProvider>
                    </div>
                    <div
                      class="input-error input-group__span-12"
                      v-if="invalid && (validated || dirty)"
                      v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
                    />
                  </ValidationObserver>

                </div>
                <div class="input-row">
                  <FormInputSelect
                    :options="countryOptions"
                    v-model="form.billingAddress.countryCode"
                    :label="$t('forms.labels.country')"
                    name="country"
                    :disabled="formLocked || billingAddressLocked"
                  />
                </div>

                <div class="input-row">
                  <ValidationProvider
                    class="input-row__wrapper"
                    name="billingPhone"
                    :rules="billingAddressLocked ? '' : 'required|phone'"
                    v-slot="{ errors, invalid, validated }"
                    tag="div"
                    :customMessages="{ required: $t('forms.errors.fieldEmpty'), phone: $t('forms.errors.phone') }"
                  >
                    <FormInput
                      type="tel"
                      :placeholder="$t('forms.labels.phone')"
                      :label="$t('forms.labels.phone')"
                      name="phone"
                      v-model="form.billingAddress.phoneNumber"
                      :error="errors.length > 0"
                      :disabled="formLocked || billingAddressLocked"
                      :required="!billingAddressLocked"
                    />
                    <span
                      class="input-error"
                      v-if="invalid && validated"
                      v-html="errors[0]"
                    />
                    <span class="additional-info" v-html="$t('checkout.phoneHint')"/>
                  </ValidationProvider>
                </div>

              </ValidationObserver>
            </div>

            <div
              v-if="showTipInput"
            >
              <h2
                class="checkout-form__block-title"
                v-html="cartTipVariant.title || $t('checkout.tipTitle')"
              />
              <div
                v-if="cartTipVariant.hint"
                class="checkout-form__block-text"
                v-html="cartTipVariant.hint"
              />

              <div class="input-row">
                <ValidationProvider
                  class="input-row__wrapper"
                  name="tip"
                  :rules="tipLocked ? '' : 'required'"
                  v-slot="{ errors, invalid, validated }"
                  tag="div"
                  :customMessages="{ required: $t('checkout.errorTip') }"
                >
                  <input type="hidden" v-model="cartTipOption">
                  <TipInput
                    :variant="cartTipVariant"
                    :options="cartTipVariant.options"
                    :selected="cartTipOption"
                    :tip="cartTip"
                    :tip-percent="cartTipPercent"
                    :locked="formLocked || tipLocked"
                    @tip:option="onTipOption"
                    @tip:percent="onTipPercent"
                    @tip:value="onTipValue"
                  />
                  <span
                    class="input-error"
                    v-if="invalid && validated"
                    v-html="errors[0]"
                  />
                </ValidationProvider>
              </div>
            </div>

            <div
              v-if="!promotionCouponLocked && !promotionCouponDisabled"
              class="checkout-form__block"
            >
              <h2 class="checkout-form__block-title" v-html="$t('checkout.couponTitle')"/>

              <div
                v-if="!isLoggedIn && (hasFeature('FEATURE_ACCOUNT') || hasFeature('FEATURE_ACCOUNT_LOGGEDIN'))"
                class="checkout-form__block-text"
              >
                <Button
                  @click.prevent="onLoginClick"
                  level="tertiary"
                  type="button"
                  :text="$t('checkout.couponLogin')"
                  arrow
                  :disabled="formLocked"
                />
              </div>
              <div
                class="input-row"
                v-else-if="isLoggedIn && accountCoupons.length"
              >
                <CheckoutUserCoupons
                  :coupons="accountCouponsEnhanced"
                  :disabled="formLocked"
                  @input="onCouponCodeSelect"
                />
              </div>

              <div class="input-row">
                <div class="input-row__wrapper">
                  <ValidationProvider
                    :rules="{regex:/.{3,40}/}"
                    ref="couponCode"
                    name="couponCode"
                    tag="div"
                    v-slot="{ invalid, errors, validated }"
                    :customMessages="{ regex: $t('forms.errors.format') }"
                  >
                    <FormInput
                      v-model="couponCode"
                      :label="$t('checkout.couponLabel')"
                      :placeholder="$t('checkout.couponLabel')"
                      :error="errors.length > 0"
                      :disabled="formLocked"
                      @keydown.enter.prevent="addCoupon"
                    />
                    <span
                      class="input-error"
                      v-if="invalid && validated"
                      v-html="errors[0]"
                    />
                    <span
                      class="input-success"
                      v-if="couponSuccess"
                      v-html="couponSuccess"
                    />
                  </ValidationProvider>
                  <Button
                    @click.prevent="addCoupon"
                    type="button"
                    level="primary"
                    :text="$t('checkout.couponButton')"
                    :disabled="formLocked"
                    small
                  />
                </div>
              </div>
              <a
                class="checkout-form__info-link"
                :href="$t('checkout.couponInfoUrl')"
                target="_blank"
              >
                <SVGInfo/>
                <span class="checkout-form__info-link-text" v-html="$t('checkout.couponInfoText')"/>
              </a>
            </div>

            <CheckoutCartButton
              show-tip
              show-co2
            />

            <div class="checkout-form__block">
              <div class="accept">
                <FormInputCheckbox
                  v-if="cartChannelHasCo2Compensation && !co2Disabled"
                  name="co2"
                  value="co2Compensation"
                  v-model="compensateCo2"
                  immediate
                  :disabled="formLocked || co2Locked"
                >
                  <span v-html="co2CheckboxLabel"/>
                </FormInputCheckbox>

                <FormInputCheckbox
                  v-if="hasFeature('FEATURE_NEWSLETTER_CHECKOUT') && showEmail"
                  name="newsletter"
                  value="newsletter"
                  v-model="form.subscribedToNewsletter"
                  :disabled="formLocked"
                  immediate
                >
                  <span v-html="$t('checkout.newsletterLabel')"/>
                </FormInputCheckbox>

                <ValidationProvider
                  v-if="cartIncludesAlcohol"
                  :rules="{ required: { allowFalse: false } }"
                  name="checkAlcohol"
                  tag="div"
                  v-slot="{ invalid, errors, validated }"
                  :customMessages="{ required: $t('checkout.errorAlcohol') }"
                >
                  <FormInputCheckbox
                    name="alcohol"
                    value="checkAlcohol"
                    v-model="form.checkAlcohol"
                    :disabled="formLocked"
                    required
                    immediate
                  >
                    <span v-html="$t('checkout.alcoholLabel')"/>
                  </FormInputCheckbox>
                  <span
                    class="input-error"
                    v-if="invalid && validated"
                    v-html="errors[0]"
                  />
                </ValidationProvider>

                <ValidationProvider
                  :rules="{ required: { allowFalse: false } }"
                  name="acceptTerms"
                  tag="div"
                  v-slot="{ invalid, errors, validated }"
                  :customMessages="{ required: $t('checkout.errorTerms') }"
                >
                  <FormInputCheckbox
                    name="terms"
                    value="acceptTerms"
                    v-model="form.acceptTerms"
                    :disabled="formLocked"
                    required
                    immediate
                  >
                    <span v-html="$t('checkout.termsLabel')"/>
                  </FormInputCheckbox>
                  <span
                    class="input-error"
                    v-if="invalid && validated"
                    v-html="errors[0]"
                  />
                </ValidationProvider>
                <ValidationProvider
                  :rules="{ required: { allowFalse: false } }"
                  name="acceptPrivacy"
                  tag="div"
                  v-slot="{ invalid, errors, validated }"
                  :customMessages="{ required: $t('checkout.errorPrivacy') }"
                >
                  <FormInputCheckbox
                    name="terms"
                    value="acceptPrivacy"
                    v-model="form.acceptPrivacy"
                    :disabled="formLocked"
                    required
                    immediate
                  >
                    <span v-html="$t('checkout.privacyLabel')"/>
                  </FormInputCheckbox>
                  <span
                    class="input-error"
                    v-if="invalid && validated"
                    v-html="errors[0]"
                  />
                </ValidationProvider>
                <!-- Currently not supported
                <fieldset class="checkbox">
                  <span class="checkbox-area">
                    <input
                      type="checkbox"
                      name=""
                      id="account"
                      class="checkbox"
                      value=""
                    />
                    <label id="" for="account"
                      >Ich möchte einen Account erstellen und meine Daten
                      speichern.</label
                    >
                  </span>
                </fieldset>
                -->

                <!-- DEBUG OUT -->
                <!--
                <span>
                  {{Object.entries(errors).map(([k, v]) => `${k}: ${v.join(",")}`).join(", ")}}
                </span>
                <span v-show="formLocked" style="color: red">FORM LOCKED</span>
                -->
                <!-- END DEBUG OUT-->
                <Button
                  type="submit"
                  level="primary"
                  :text="$t('checkout.orderButton')"
                  :disabled="formLocked"
                />

                <div>
                  <Button
                    v-if="!cartIsDetached"
                    @click.prevent="backToShop"
                    class="checkout-form__back-to-shop checkout-form__back-to-shop--bottom"
                    level="tertiary"
                    type="button"
                    :text="$t('checkout.continueShopping')"
                    arrow
                    reverse
                    :disabled="formLocked"
                  />
                </div>
              </div>
            </div>
          </template>
				</ValidationObserver>

			</div>
		</div>
		<div class="checkout-form__cart-placeholder">
			<OverlaySidebarCart
				class="insicht insicht--fade-up insicht--fast"
				ref="sidebarCart"
				:is-editable="false"
				is-inline
				show-co2
				show-tip
			/>
		</div>
	</div>
</template>

<script>
import Vue from "vue"
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputPayment from "@/components/FormInputPayment"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputRadio from "@/components/FormInputRadio"
import FormInputSelect from "@/components/FormInputSelectCustom"
import FormInputRange from "@/components/FormInputRange"
import veevalidate from "@/helpers/veevalidate"
import { dayjs, isSameDay, dayjsLocal } from "@/helpers/dayjs"
import OverlaySidebarCart from '@/pages/OverlaySidebarCart'
import CheckoutCartButton from '@/components/CheckoutCartButton'
import CheckoutUserCoupons from '@/components/CheckoutUserCoupons'
import CheckoutSavedPayments from '@/components/CheckoutSavedPayments'
import TipInput from '@/components/TipInput'
import GeneralMessage from '@/components/GeneralMessage'
import Button from '@/components/Button'
import ShopHeader from '@/components/ShopHeader';
import MapLocations from '@/components/MapLocations'
import scrrroll from 'scrrroll'
import {eventhub, EVENT_ACCOUNT_ISLOGGEDIN} from '@/store/eventhub'
import checkoutPrefill from '@/helpers/checkoutPrefill'
import htmlComponent from "htmlcomponent"
import CartOrderMixin from '@/mixins/CartOrderVueMixin';
import GenericMixin from '@/mixins/GenericVueMixin'
import {getUrlParams, makeUrl} from '@/utils/urlParams';
import { formatAddress, formatLines } from "@/utils/format"

export default {
	mixins: [
		CartOrderMixin,
    GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputTextarea,
		FormInputPayment,
		FormInputCheckbox,
		FormInputRadio,
		FormInputSelect,
		FormInputRange,
		OverlaySidebarCart,
		CheckoutCartButton,
		CheckoutUserCoupons,
		CheckoutSavedPayments,
		TipInput,
		GeneralMessage,
		Button,
		ShopHeader,
    MapLocations,
	},
	data() {
		return {
			countryOptions: [
				{
					value: 'CH',
					label: this.$t('forms.countries.ch')
				}
			],
			form: {
				email: "",
				shippingAddress: {
					firstName: "",
					lastName: "",
					countryCode: "CH",
					street: "",
					streetNumber: "",
					details: "",
					city: "",
					postcode: "",
					provinceName: "",
					company: "",
					phoneNumber: "",
				},
				billingAddressIdentical: true,
				billingAddress: {
					firstName: "",
					lastName: "",
					countryCode: "CH",
					street: "",
					streetNumber: "",
					city: "",
					postcode: "",
					provinceName: "",
					company: "",
					phoneNumber: ""
				},
				paymentCode: '',
				tip: 0,
				shippingId: 0,
				voucherCode: "",
				notes: '',
				acceptPrivacy: false,
				acceptTerms: false,
				checkAccount: false,
				checkAlcohol: false,
				dropoffType: null,
				compensateCo2: this.cartCo2Compensation,
				subscribedToNewsletter: false,
				useDishes: false,
				dishReturnMethod: 'reorder',
        createUser: false,
        anonymousDonation: false,
			},
			couponCode: "",
			couponSuccess: '',
			debug: false,
			dropoffOptions: [1,2].map(option => {
				return {
					value: option,
					label: this.$t(`checkout.dropoff.${option}`),
				};
			}),
			showPaymentMethods: true,
			selectedSavedPaymentToken: null,
			formSubmitted: false,
			checkoutMessageDialogs: [],
			checkoutMessageDialogChain: Promise.resolve(),
      addressSet: false,
			deliverySet: false,
		};
	},
	computed: {
		...mapGetters("checkout", [
			"checkoutPayments",
      "checkoutPaymentTipExcludeIds",
			"checkoutFormData",
			"checkoutBasketZip",
			"checkoutDishReturnMethods",
			'savedPayments',
			'checkoutMessages'
		]),
		...mapGetters("notification", [
			"notifyMessages"
		]),
		...mapGetters("cart", [
			"discountCode",
			"deliveryTime",
			"deliveryPrice",
			"cartDeliveryTime",
			"cartIncludesAlcohol",
			"shippingMethod",
			"cartOrderTimePassed",
			"cartOrderNow",
			"cartTip",
			"cartTipEnabled",
			"cartTipDefault",
      "cartTipTypes",
      "cartTipType",
      "cartTipPercent",
      "cartTipVariant",
      "cartTipOption",
			"cartChannelHasCo2Compensation",
			"cartCo2Compensation",
			"cartCo2CompensationAmount",
			"cartCo2CompensationRatio",
			"cartBigOrderText",
			"cartChannelHasDishesEnabled",
			"cartHasItemsWithDishEnabled",
			"cartIsDetached",
			"orderStates",
			"orderType",
			"tokenValue",
			"cartOutdoorDeliveryPoint",
			'cartIsGroupOrder',
			'cartIsGroupOrderLocked',
      'cartGlobalMessage',
      "cartIsUberDirect",
      "cartHasAddress",
      "cartShippingCoordinates",
      "cartWarnings",
		]),
		...mapGetters("grouporder", [
			'groupOrderIsOwner',
		]),
		...mapGetters("account", [
			"isLoggedIn",
			"accountLastAddress",
			"accountUserDetail",
			"accountCoupons",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		formLocked() {
			return this.loading || this.formSubmitted;
		},
		deliveryTimeEnhanced() {
			return this.cartOrderNow
				? this.$t('cart.orderNowTime', { minutes: this.deliveryTime })
				: this.cartDeliveryTime
					? `${this.cartDeliveryTime.format('L')} | ${this.cartDeliveryTime.format('LT')}`
					: '';
		},
		selectedPaymentMethod () {
			if (this.form.paymentCode) {
				return this.checkoutPayments.find(p => p.code == this.form.paymentCode)
			}
			return null
		},
		selectedSavedPayment() {
			if (this.selectedSavedPaymentToken) {
				return this.savedPayments.find(payment => payment.token == this.selectedSavedPaymentToken)
			}
			return null
		},
		showSavedPayments() {
			return this.savedPayments.length && this.checkoutPayments.length && this.checkoutPayments.some(payment => payment.code.startsWith('wallee'))
		},
		accountCouponsEnhanced() {
			return this.accountCoupons
				.map(coupon => {
					return {
						...coupon,
						active: coupon.code == this.discountCode,
					};
				});
		},
		showTipInput() {
			return this.cartTipEnabled && this.form.paymentCode && !this.checkoutPaymentTipExcludeIds.includes(this.form.paymentCode) && !this.tipLocked && !this.tipDisabled;
		},
		showAddress() {
			return !this.isIndining && !this.isOutdoorDelivery;
		},
		showBillingAddress() {
			return this.selectedPaymentMethod && this.selectedPaymentMethod.displayInvoiceAddress;
		},
		addressTitle() {
			return this.isDelivery ? this.$t('checkout.shippingAddress') : this.$t('checkout.address');
		},
		timeTitle() {
			return this.isDelivery ? this.$t('checkout.timeTitleDelivery') : this.isIndining ? this.$t('checkout.timeTitleIndining') : this.$t('checkout.timeTitleTakeaway');
		},
		timeEditLabel() {
			return this.isDelivery ? this.$t('checkout.changeDeliveryTime') : this.$t('checkout.changeTakeawayTime');
		},
		addressRequired() {
			return (this.isDelivery || this.checkoutDishReturnMethods.find(v => v.id == this.form.dishReturnMethod)?.requireAddress || false) && !this.shippingAddressLocked;
		},
		compensateCo2: {
			get() {
				return this.cartCo2Compensation;
			},
			set(value) {
				this.form.compensateCo2 = value;
				this.$store.dispatch('cart/setCo2Compensation', { value });
			}
		},
		co2CheckboxLabel() {
			let amount = this.$options.filters.fwgpricecent(this.cartCo2CompensationAmount);
			if (this.cartCo2CompensationRatio) {
				amount = `${this.cartCo2CompensationRatio * 100}%`;
			}
			return this.$t('checkout.co2CompensationLabel', { amount });
		},
    checkoutPaymentsVisible() {
      return this.checkoutPayments
        .filter(p => p.visible !== false)
    },
		// dishReturnMethods (state, getters) {
		// 	return this.checkoutDishReturnMethods.map(option => {
		// 		let hidden = false;
		// 		if (option.shippingMethods) {
		// 			hidden = true;

		// 			if (
		// 				option.shippingMethods.includes('delivery') && this.isDelivery ||
		// 				option.shippingMethods.includes('takeaway') && this.isTakeaway ||
		// 				option.shippingMethods.includes('indining') && this.isIndining
		// 			) {
		// 				hidden = false;
		// 			}
		// 		}

		// 		return {
		// 			code: option.id,
		// 			icon: option.icon,
		// 			name: this.$t(`checkout.reusableDishes.returnMethods.${option.id}`),
		// 			description: this.$te(`checkout.reusableDishes.returnMethods.${option.id}Description`)
		// 				? this.$t(`checkout.reusableDishes.returnMethods.${option.id}Description`)
		// 				: '',
		// 			instructions: this.$te(`checkout.reusableDishes.returnMethods.${option.id}Instructions`)
		// 				? this.$t(`checkout.reusableDishes.returnMethods.${option.id}Instructions`)
		// 				: '',
		// 			hidden,
		// 		};
		// 	})
		// },
		showEmail() {
			return true // HACK: !this.isAnonymous || !!this.isLoggedIn;
		},
		showReusableDishes() {
			return (
				this.hasFeature('FEATURE_REUSABLE_DISHES') &&
				this.cartChannelHasDishesEnabled &&
				this.cartHasItemsWithDishEnabled &&
				(
					(!this.reusableDishDisabled && !this.reusableDishLocked) ||
					(this.reusableDishLocked && this.form.useDishes)
				)
			)
		},
		zipLocked() {
			return this.isDelivery && (this.isOrderTypeMain || this.isOrderTypeGastroKurier);
		},
		outdoorDeliveryAddress() {
			if (!this.isOutdoorDelivery) {
				return;
			}
			const { title, street, streetNumber, postcode, city, latitude, longitude } = this.cartOutdoorDeliveryPoint
			const lines = []
			lines.push(title)
			lines.push([street, streetNumber].join(' '))
			lines.push([postcode, city].join(' '))
			// lines.push(`<a href="http://www.google.com/maps/place/${latitude},${longitude}" target="_blank">${this.$t('checkout.outdoorDelivery.googleMapsLink')}</a>`)
			return lines.join('<br>')
		},
		checkoutMessagesNormal() {
			return this.checkoutMessages.filter(m => m.type !== 'alert')
		},
		checkoutMessagesDialog() {
			return this.checkoutMessages.filter(m => m.type === 'alert')
		},
    addressDetailLimit() {
      return !!this.cartIsUberDirect ? 200 : undefined
    },
    notesLimit() {
      // not needed at this moment
      return undefined
    },
    showAddressDetailsHint() {
      return (
        this.isOrderTypeMain
        || this.isOrderTypeGastroKurier
      ) && this.isDelivery
    }
	},
	watch: {
		couponCode(val) {
			if (val) {
				this.couponSuccess = '';
			}
		},
		cartOrderTimePassed(val) {
			if (val) {
				this.onTimeEdit();
			}
		},
		// cartHasItemsWithDishEnabled() {
		// 	this.form.useDishes = false;
		// },
		// 'form.useDishes'() {
		// 	this.form.dishReturnMethod = null;
		// },
		'form.paymentCode'() {
			if (!this.showTipInput) {
				this.$store.dispatch('cart/setTipPercent', { value: 0 })
				this.$store.dispatch('cart/adjustTip', { value: 0 })
			}
		},
		selectedSavedPaymentToken(val) {
			if (val) {
				this.form.paymentCode = 'wallee';
				this.showPaymentMethods = false;
			}
		},
		checkoutPayments(payments) {
			if (payments.length === 1) {
				this.form.paymentCode = payments[0].code
			}
		},
		checkoutMessagesDialog(messages) {
			messages.forEach((message) => {
				if (!this.checkoutMessageDialogs.includes(message.message)) {
					this.checkoutMessageDialogs.push(message.message)
					this.checkoutMessageDialogChain = this.checkoutMessageDialogChain.then(() => this.$store.dispatch('dialog/showDialog', {
						title: this.$t('forms.errors.error'),
						message: message.message,
						confirm: 'OK',
					}))
				}
			})
		}
	},
	methods: {
    formatAddress,
    formatLines,
		backToShop() {
			this.$store.dispatch('redirect', {name: 'shop'})
		},
		onLoginClick() {
			htmlComponent.send('overlay:open', {type: 'login'});
		},

		onZipKeydown(e) {
			if (!e.metaKey && !e.ctrlKey && e.key && e.key.length <= 1 && !/[0-9]/.test(e.key)) {
				e.preventDefault()
			}
		},
		onZipInput(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form.shippingAddress, 'postcode', value.replace(/[^\d]/ig,'').substring(0,4));
				this.$refs.shippingZipInput.$refs.input.value = this.form.shippingAddress.postcode;
			}
		},
		onZipInputBilling(value) {
			if (/[^\d]/.test(value)) {
				Vue.set(this.form.billingAddress, 'postcode', value.replace(/[^\d]/ig,'').substring(0,4));
				this.$refs.billingZipInput.$refs.input.value = this.form.billingAddress.postcode;
			}
		},

    onShippingAddressEdit() {
			this.addressSet = false;
		},

		async onShippingAddress() {
			this.loading = true
			const isValid = await this.$refs.shippingAddressForm.validate()
			if (isValid) {
				// this.$store.dispatch('notification/clearMessages')
				try {
					// If billing address has been entered, but is not necessary anymore, overwrite it with the shipping address
					if ((this.showBillingAddress && this.form.billingAddressIdentical) || !this.showBillingAddress) {
						Object.keys(this.form.billingAddress).forEach(key => {
							Vue.set(this.form.billingAddress, key, this.form.shippingAddress[key])
						})
					}

					await this.$store.dispatch('setShippingAddress', {
						form: {
							...this.form
						}
					})

					this.addressSet = true;
					this.loading = false;
					// await this.$nextTick();
					// await scrrroll.to(this.$refs.deliveryTimeForm.$el);
				} catch (err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.onShippingAddress(), retry: true })
          } else {
            let notificationMessage = err.response.data
            if (typeof notificationMessage === "object") {
              let errors = notificationMessage.errors
              if (errors) {
                //handle errors
                notificationMessage = Object.values(errors)
                  .map(v => v[0])
                  .join(", ")
              } else {
                notificationMessage = notificationMessage.message
              }
            }
            this.$store.dispatch('notification/addMessage', {
              message: {
                message: notificationMessage || this.$t('forms.errors.generic'),
                type: "error"
              }
            })
            await this.$nextTick()
            let visibleNotification = this.$refs.notificationMessages.find(v => v.visible)
            if (visibleNotification) {
              scrrroll.to(visibleNotification.$el)
            }
          }

					this.loading = false
				}
			} else {
				let notifications = Object
					.entries(this.$refs.shippingAddressForm.errors)
					.filter(([key, val]) => val != null && val.length > 0)
					.filter((v, i) => i == 0)
					.map(([key, val]) => key)

				let validator = this.$refs.shippingAddressForm.refs[notifications[0]] || this.$refs[notifications[0]]
				if (validator) {
					scrrroll.to(validator.$el)
				}
				this.loading = false
			}
		},

		onTimeEdit() {
			this.$refs.sidebarCart.onTimeEdit(true);
		},

		async onShowPaymentMethods() {
			this.showPaymentMethods = true;
			this.selectedSavedPaymentToken = null;
			this.form.paymentCode = null;
		},

		async onDeletePayment(payment) {
			const confirmed = await this.$store.dispatch('dialog/showDialog', {
				title: this.$t('checkout.savedPayments.deleteDialogTitle'),
				message: this.$t('checkout.savedPayments.deleteDialogText'),
				confirm: this.$t('checkout.savedPayments.deleteDialogConfirm'),
				cancel: this.$t('checkout.savedPayments.deleteDialogCancel'),
			})

			if (confirmed) {
				this.loading = true
				if (this.selectedSavedPaymentToken == payment.token) {
					this.selectedSavedPaymentToken = null
				}
				await this.$store.dispatch('checkout/deleteSavedPayment', { token: payment.token })
				this.loading = false
			}
		},

		async submit() {
			this.loading = true
			const isValid = await this.$refs.form.validate()
			if (isValid) {
				this.$store.dispatch('notification/clearMessages')

				this.formSubmitted = true;
				try {
					// reset tip before submit if its not visible
					if ((this.showTipInput || this.tipLocked) && this.cartTip > 0 && !this.tipDisabled) {
						this.form.tip = this.cartTip
					} else {
						this.form.tip = 0
					}

					// If billing address has been entered, but is not necessary anymore, overwrite it with the shipping address
					if ((this.showBillingAddress && this.form.billingAddressIdentical && this.isDelivery) || !this.showBillingAddress) {
						Object.keys(this.form.billingAddress).forEach(key => {
							Vue.set(this.form.billingAddress, key, this.form.shippingAddress[key]);
						});
					}

					if (this.isIndining) {
						// clear hidden address fields for indining
						['company', 'street', 'streetNumber', 'postcode', 'city', 'details'].forEach(key => {
							Vue.set(this.form.shippingAddress, key, '');
							Vue.set(this.form.billingAddress, key, '');
						});
					}

          const anonymousDonationNote = this.$t('checkout.anonymouDonationNote')
          let notes = this.form.notes

          if (this.form.anonymousDonation) {
            if (!notes.includes(anonymousDonationNote)) {
              notes = notes + (notes ? '\n' : '') + anonymousDonationNote
            }
          } else {
            notes = notes.replace(anonymousDonationNote, '')
          }

					await this.$store.dispatch('submitOrder', {
						form: {
							...this.form,
              notes
						},
						deliverTime: this.cartDeliveryTime,
						savedPaymentToken: this.selectedSavedPaymentToken
					})
				} catch(err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.submit(), retry: true })
          } else {
            if (err?.type === 'timeout') {
              return this.onFormTimeout(err)
            }

            this.formSubmitted = false;
            this.isDebug() && console.error("PageCheckout::submit", err)

            //TODO: we have to handle a lot of different error formats... crap...
            let notificationMessage = err?.response?.data ?? err.message
            if (typeof notificationMessage === "object") {
              let errors = notificationMessage.errors
              if (errors) {
                //handle errors
                notificationMessage = Object.values(errors)
                  .map(v => v[0])
                  .join(", ")
              } else {
                notificationMessage = notificationMessage.message
              }
            }
            this.$store.dispatch('notification/addMessage', {
              message: {
                message: notificationMessage || this.$t('forms.errors.generic'),
                type: "error"
              }
            })
            await this.$nextTick()
            let visibleNotification = this.$refs.notificationMessages.find(v => v.visible)
            if (visibleNotification) {
              visibleNotification.scrollTo();
            }
          }

					this.loading = false
				}
			} else {
				await this.$nextTick();
				const inputError = this.$refs.form.$el.querySelector('.input-error');

				if (inputError) {
					scrrroll.to(inputError.parentNode)
				}
				this.loading = false
			}
		},
		onCouponCodeSelect(value) {
			this.couponCode = value;
			this.addCoupon();
		},
		onPaymentChanged(value) {
			if (!this.showTipInput) {
				this.$store.dispatch('cart/setTipPercent', { value: 0 })
				this.$store.dispatch('cart/adjustTip', { value: 0 })
			}
		},
		async addCoupon() {
			this.loading = true
			this.couponSuccess = '';
			const validateResult = await this.$refs.couponCode.validate()
			if (this.couponCode && validateResult.valid) {
				let couponResult = await this.$store.dispatch('cart/addCoupon',{coupon: this.couponCode, email: this.form.email, useDishes: this.form.useDishes})
					.catch(err => Promise.resolve({error: true, ...err}))
				if (couponResult?.error) {
          if (couponResult?.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.addCoupon(), retry: true })
          } else {
            this.$refs.couponCode.setErrors(couponResult.response?.data?.errors?.coupon?.length ? couponResult.response.data.errors.coupon : [this.$t('forms.errors.generic')])
          }
				} else {
					//update payments
					await this.$store.dispatch('checkout/payments')
					this.couponCode = '';
					this.couponSuccess = this.$t('checkout.successCoupon');
				}
			}
			this.loading = false
		},
		isDateDisabled(date) {
			const dates = this.checkoutOrderDisabledDates
			const check = dayjsLocal(date)
			return dates.find(d => isSameDay(d, check))
		},
		async onIsLoggedIn() {
			this.loading = true;
			this.prefillFormdata()
			await this.$store.dispatch('checkout/savedPayments')
			this.loading = false;
		},
		prefillFormdata() {
			let formData = this.checkoutFormData

			if (formData?.compensateCo2) {
				this.compensateCo2 = formData.compensateCo2;
			}

      this.addressSet = this.cartHasAddress

      const anonymousDonationNote = this.$t('checkout.anonymouDonationNote')

      if (formData?.notes?.includes(anonymousDonationNote)) {
        const reg = new RegExp('\n?'+anonymousDonationNote)
        formData.notes = formData.notes.replace(reg, '')
        Vue.set(this.form, 'anonymousDonation', true)
      }

			// TODO: we outsorced this part, but we have to provide all access to this function
			checkoutPrefill({
				formData,
				accountUserDetail: this.accountUserDetail,
				accountLastAddress: this.accountLastAddress,
				cartZip: this.checkoutBasketZip,
				form: this.form,
				$store: this.$store,
				isDelivery: this.zipLocked,
			})

			this.loading = false
		},
		onTipOption(value) {
      const [optionIndex, subOptionIndex] = value?.toString().split('-') ?? []
			let option = this.cartTipVariant?.options?.[optionIndex]

      if (option?.options?.length && subOptionIndex != null) {
        option = option.options[subOptionIndex]
      }

			this.$store.dispatch('cart/setTipOption', { value: value })
			this.$store.dispatch('cart/setTipType', { value: option?.type })
		},
		onTipPercent(value) {
			this.$store.dispatch('cart/setTipPercent', { value })
		},
		onTipValue(value) {
			this.$store.dispatch('cart/adjustTip', { value })
		},
		async onFormTimeout(error) {
      if (document.querySelector('#paymentFrameWrapper')) {
        return
			}

      this.isDebug() && console.error(error)
			this.loading = false
			this.$store.dispatch('checkout/escalateTimeout', { message: error?.message })

			await this.$store.dispatch('dialog/showDialog', {
				title: this.$t('checkout.payment.timeoutDialogTitle'),
				message: this.$t('checkout.payment.timeoutDialogText'),
				confirm: this.$t('checkout.payment.timeoutDialogConfirm'),
			})

			await this.$store.dispatch('redirect', {
				name: "checkout-cancel",
				params: { basketToken: this.tokenValue }
			})
		}
	},
	mounted() {
		eventhub.$on(EVENT_ACCOUNT_ISLOGGEDIN, this.onIsLoggedIn)

		// TODO: if cart time is not possible anymore
		if (this.cartOrderTimePassed) {
			this.onTimeEdit();
		}
	},
	destroyed() {
		eventhub.$off(EVENT_ACCOUNT_ISLOGGEDIN, this.onIsLoggedIn)
	},
	async created() {
		this.loading = true;
		let {tokenValue: token, cancel, error} = getUrlParams();

    try {
      await this.$store.dispatch('initializeCheckout')
    } catch(err) {
      if (err.isMaintenanceError) {
        this.onMaintenanceMode()
      }
    }

		this.prefillFormdata()

		if (cancel || error) {
			history.replaceState({}, null, makeUrl({ tokenValue: this.tokenValue }));

			if (cancel) {
				this.$store.dispatch('notification/addMessage', {
					message: {
						message: this.$t('checkout.payment.cancel'),
						type: "error"
					}
				})
			}
			else if (error) {
				this.$store.dispatch('notification/addMessage', {
					message: {
						message: this.$t('checkout.payment.error'),
						type: "error"
					}
				})
			}
		}

		if (this.cartIsGroupOrder && this.groupOrderIsOwner) {
			await this.$store.dispatch('cart/lockGroupOrder', { locked: true })
		}

		this.loading = false;
	}
}
</script>

<style></style>
